import styled from '@emotion/styled'
import {
  DoseDispensingProductsListItemDto,
  ExtranetDoseDispensingProductMetadataDto,
  ExtranetPhotobankProductDto,
} from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetFullScreenImage,
  MediaQuery,
  htmlStringSanitizer,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButtonBase,
  PharmacCarousel,
  PharmacCarouselItem,
  PharmacH4,
  PharmacIcon,
  PharmacImage,
  PharmacLink,
  PharmacText,
  PharmacTruncate,
} from '@pharmac/ui'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../services/HttpService'
import { CatalogItemAvailability } from './CatalogItemAvailability'
import { CatalogItemHalving } from './CatalogItemHalving'
import ImageNotAvailable from './image-not-available/NoImageAvailable_16_9.jpg'

const CarouselImage = styled(PharmacImage)`
  max-width: 75% !important;
`

const ImgContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  cursor: zoom-in;
`

const FullScreenIcon = styled(PharmacIcon)`
  position: relative;
  background-color: white;
  right: 1.5rem;
`

const HR = styled.hr`
  border: 1px dashed var(--color-accent-4);
  width: 100%;
  margin-top: var(--spacing-large);
`
const ShowAdditionalInfoButton = styled(PharmacButtonBase)`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding-top: var(--spacing-small);
  padding-bottom: var(--spacing-small);
  justify-content: flex-end;
  align-items: center;
`

const MoreIcon = styled(PharmacIcon)`
  color: var(--color-primary);
  margin-top: 2px;
  margin-left: 0.5rem;
  margin-right: 1rem;
`

const AdditionalInfoContainer = styled.div<{ isOpen?: boolean }>`
  display: ${(p) => (p.isOpen ? 'flex' : 'none')};
  width: 100%;
  flex-direction: column;
`

const SubstituteProductRoot = styled.div`
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-accent-5);

  height: 70px;

  width: 100%;

  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
  background-color: var(--color-white);
  font-weight: 500;
`

const SubstituteProductIconContainer = styled(ExtranetContainer)`
  height: 100%;
  width: 90px;
  text-align: center;

  background-color: var(--extranet-light-purple);

  ${MediaQuery.maxMobile} {
    max-width: 30px;
    width: 100%;

    svg {
      width: 15px;
      height: 15px;
    }
  }
`

const SubtituteProductVnrContainer = styled.div`
  width: 100px;
  text-align: center;

  ${MediaQuery.maxMobile} {
    max-width: 75px;
    width: 100%;
  }
`

const SubstituteProductNameContainer = styled.div``
const SubtsituteProductName = styled.div``
const SubstituteProductStrength = styled.div``

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: var(--spacing-medium);
`

type SubstituteProductProps = {
  product: DoseDispensingProductsListItemDto
  handleSubstituteSearch: (v: string) => void
}

const SubstituteProduct = ({ product, handleSubstituteSearch }: SubstituteProductProps) => {
  const { clientWidth } = useBreakpoint()

  const truncate = clientWidth < 450 ? 15 : clientWidth < 600 ? 50 : 80

  return (
    <SubstituteProductRoot>
      <SubstituteProductIconContainer
        flexRow
        justifyCenter
        alignCenter
      >
        <PharmacIcon icon="refreshCcw" />
      </SubstituteProductIconContainer>
      <SubtituteProductVnrContainer>
        <PharmacLink
          onClick={() => product.productNumber && handleSubstituteSearch(product.productNumber)}
          style={{ textDecoration: 'underline', fontWeight: 500 }}
        >
          {product.productNumber}
        </PharmacLink>
      </SubtituteProductVnrContainer>
      <SubstituteProductNameContainer>
        <PharmacLink
          onClick={() => product.productNumber && handleSubstituteSearch(product.productNumber)}
          style={{ textDecoration: 'underline', fontWeight: 500 }}
        >
          <SubtsituteProductName>{PharmacTruncate(product.name, truncate)}</SubtsituteProductName>
          <SubstituteProductStrength>{product.strength}</SubstituteProductStrength>
        </PharmacLink>
      </SubstituteProductNameContainer>
    </SubstituteProductRoot>
  )
}

type Props = {
  product: DoseDispensingProductsListItemDto
  handleSubstituteSearch: (v: string) => void
}

export const CatalogItemDetails = ({ product, handleSubstituteSearch }: Props) => {
  const { t, key } = useLanguage(['extranet', 'common'])
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [fullScreenUrl, setFullScreenUrl] = useState<string | null>(null)
  const showFullScreenImage = (url: string) => setFullScreenUrl(url)
  const closeFullScreenImage = () => setFullScreenUrl(null)

  const { minTablet, minDesktop } = useBreakpoint()

  const getMetadata = (
    vnr?: string
  ): UseQueryOptions<ExtranetDoseDispensingProductMetadataDto> => ({
    queryFn: async () => await api.products.getDoseDispensingProductMetadata(vnr as string),
    queryKey: ['__productMetadata', product.productNumber],
    enabled: typeof vnr === 'string',
  })

  const getPhotobankData = (vnr?: string): UseQueryOptions<ExtranetPhotobankProductDto> => ({
    queryFn: async () => await api.products.getPhotobankData(vnr as string),
    queryKey: ['__photobankData', product.productNumber],
    enabled: typeof vnr === 'string',
  })

  const { data: productMetadata } = useQuery(getMetadata(product.productNumber))
  const { data: photobankData } = useQuery(getPhotobankData(product.productNumber))

  const medicineImages = productMetadata?.pharmacaFullData?.images?.map((a) => a)

  const isCrushable = (crushable?: string) => {
    if (crushable === '0') return t('extranet:medicines.isCrushable.0')
    if (crushable === '1') return t('extranet:medicines.isCrushable.1')
    if (crushable === '9') return t('extranet:medicines.isCrushable.9')
    return crushable
  }

  const isLactose = (lactose?: string) => {
    if (lactose === '0') return t('extranet:medicines.isLactose.0')
    if (lactose === '1') return t('extranet:medicines.isLactose.1')
    return lactose
  }

  const isSplittable = (splittable?: string) => {
    if (splittable === '0') return t('extranet:medicines.isSplittable.0')
    if (splittable === '1') return t('extranet:medicines.isSplittable.1')
    if (splittable === '2') return t('extranet:medicines.isSplittable.2')
    if (splittable === '3') return t('extranet:medicines.isSplittable.3')
    if (splittable === '4') return t('extranet:medicines.isSplittable.4')
    if (splittable === '5') return t('extranet:medicines.isSplittable.5')
    if (splittable === '6') return t('extranet:medicines.isSplittable.6')
    if (splittable === '7') return t('extranet:medicines.isSplittable.7')
    if (splittable === '9') return t('extranet:medicines.isSplittable.9')
    return splittable
  }

  const isTrafficHazard = (trafficHazard?: string) => {
    if (trafficHazard === '0') return t('common:no')
    if (trafficHazard === '1') return t('common:yes')
    if (trafficHazard === '9') return t('extranet:medicines.unknown')
    return trafficHazard
  }

  const isNarcotic = (narcotic?: string) => {
    if (narcotic === '0') return t('common:no')
    if (narcotic === '1') return t('common:yes')
    return narcotic
  }

  return (
    <ExtranetContainer
      full
      flexColumn
    >
      {!minDesktop && (
        <>
          <ExtranetContainer
            full
            flexColumn
            paddingLargeLeft
            paddingLargeTop
          >
            <ExtranetContainer
              full
              flexColumn
              justifyStart
              paddingSmallVertical
            >
              <PharmacText>{t('medicines.strength')}</PharmacText>
              <PharmacText
                weight="bold"
                marginSmallTop
              >
                {product.strength}
              </PharmacText>
            </ExtranetContainer>
            <ExtranetContainer
              full
              flexColumn
              justifyStart
              paddingSmallVertical
            >
              <PharmacText>{t('medicines.activeSubstancesDescription')}</PharmacText>
              <PharmacText
                weight="bold"
                marginSmallTop
              >
                {product.activeSubstances}
              </PharmacText>
            </ExtranetContainer>
            <ExtranetContainer
              full
              flexColumn
              justifyStart
              paddingSmallVertical
            >
              <PharmacText>{t('medicines.manufacturerName')}</PharmacText>
              <PharmacText
                weight="bold"
                marginSmallTop
              >
                {product.manufacturerName}
              </PharmacText>
            </ExtranetContainer>
            <ExtranetContainer
              full
              flexColumn
              justifyStart
              paddingSmallVertical
            >
              <PharmacText marginSmallBottom>{t('medicines.availableForCustomers')}</PharmacText>
              <CatalogItemAvailability availability={product.availability} />
            </ExtranetContainer>
            {product.halving !== 'No' && (
              <ExtranetContainer
                full
                flexColumn
                justifyStart
                paddingSmallVertical
              >
                <PharmacText marginSmallBottom>{t('medicines.halving')}</PharmacText>
                <CatalogItemHalving halving={product.halving} />
              </ExtranetContainer>
            )}
            {product.substituteProduct && (
              <ExtranetContainer
                full
                flexColumn
                justifyCenter
                alignStart
                paddingLargeRight
                paddingMediumTop
              >
                <ExtranetContainer
                  full
                  flexColumn
                >
                  <PharmacText paddingSmallBottom>{t('medicines.substituteProduct')}</PharmacText>
                  <SubstituteProduct
                    handleSubstituteSearch={handleSubstituteSearch}
                    product={product.substituteProduct}
                  />
                </ExtranetContainer>
              </ExtranetContainer>
            )}
          </ExtranetContainer>
          <HR />
          <ExtranetContainer
            full
            flexRow
            justifyEnd
          >
            <ShowAdditionalInfoButton onClick={() => setIsOpen(!isOpen)}>
              <PharmacText>
                {t(isOpen ? 'medicines.closeMoreInfo' : 'medicines.showMoreInfo')}
              </PharmacText>
              <MoreIcon icon={isOpen ? 'chevronUp' : 'chevronDown'} />
            </ShowAdditionalInfoButton>
          </ExtranetContainer>
        </>
      )}
      <AdditionalInfoContainer isOpen={isOpen || minDesktop}>
        <ExtranetContainer
          full
          flexRow={minDesktop}
          flexColumn={!minDesktop}
        >
          <ExtranetContainer
            half={minDesktop}
            full={!minDesktop}
            paddingLargeHorizontal
            flexColumn
          >
            <ExtranetContainer
              full
              flexRow
              paddingLargeTop
            >
              <ExtranetContainer
                half
                flexColumn
              >
                <ExtranetContainer
                  full
                  flexColumn
                  paddingSmallVertical
                >
                  <PharmacText weight="bold">{t('medicines.drugFormatDescription')}:</PharmacText>
                  {product.format ? (
                    <PharmacText>{product.format}</PharmacText>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </ExtranetContainer>
                <ExtranetContainer
                  full
                  flexColumn
                  paddingSmallVertical
                >
                  <PharmacText weight="bold">{t('medicines.tabletSplit')}:</PharmacText>
                  {product.seamType ? (
                    <PharmacText>{isSplittable(product.seamType)}</PharmacText>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </ExtranetContainer>
                <ExtranetContainer
                  full
                  flexColumn
                  paddingSmallVertical
                >
                  <PharmacText weight="bold">{t('medicines.crushable')}:</PharmacText>
                  {productMetadata?.pharmacaFullData?.crushable ? (
                    <PharmacText>
                      {isCrushable(productMetadata?.pharmacaFullData?.crushable)}
                    </PharmacText>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </ExtranetContainer>
                <ExtranetContainer
                  full
                  flexColumn
                  paddingSmallVertical
                >
                  <PharmacText weight="bold">{t('medicines.lactose')}:</PharmacText>
                  {productMetadata?.pharmacaFullData?.lactose ? (
                    <PharmacText>
                      {isLactose(productMetadata?.pharmacaFullData?.lactose)}
                    </PharmacText>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </ExtranetContainer>
              </ExtranetContainer>
              <ExtranetContainer
                half
                flexColumn
              >
                {/* TODO: add back when available from ERP */}
                {/* <ExtranetContainer
                full
                flexColumn
                paddingSmallVertical
              >
                <PharmacText>{t('extranet:medicines.pharmaceuticalGroup')}:</PharmacText>
                <PharmacText weight="bold">Särkylääkkeet</PharmacText>
              </ExtranetContainer> */}
                <ExtranetContainer
                  full
                  flexColumn
                  paddingSmallVertical
                >
                  <PharmacText weight="bold">{t('extranet:medicines.drug')}:</PharmacText>
                  {product.narcoticType ? (
                    <PharmacText>{isNarcotic(product.narcoticType)}</PharmacText>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </ExtranetContainer>
                <ExtranetContainer
                  full
                  flexColumn
                  paddingSmallVertical
                >
                  <PharmacText weight="bold">{t('extranet:medicines.psychoClass')}:</PharmacText>
                  {product.centralNervousSystemDrugType ? (
                    <PharmacText>
                      {`${t('common:yes')} (${product.centralNervousSystemDrugType})`}
                    </PharmacText>
                  ) : (
                    <PharmacText>{t('common:no')}</PharmacText>
                  )}
                </ExtranetContainer>
                <ExtranetContainer
                  full
                  flexColumn
                  paddingSmallVertical
                >
                  <PharmacText weight="bold">{t('extranet:medicines.trafficHazard')}:</PharmacText>
                  {product.trafficHazardType ? (
                    <PharmacText>{isTrafficHazard(product.trafficHazardType)}</PharmacText>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </ExtranetContainer>
              </ExtranetContainer>
            </ExtranetContainer>

            <ExtranetContainer
              full
              flexColumn
              paddingSmallTop
            >
              <ExtranetContainer
                full
                flexColumn
              >
                <ExtranetContainer
                  full
                  flexColumn
                  paddingSmallVertical
                >
                  <PharmacText weight="bold">
                    {t('medicines.additionalDosageInformation')}:
                  </PharmacText>
                  {productMetadata?.pharmacaFullData?.additionalDosageInformation &&
                  productMetadata?.pharmacaFullData?.additionalDosageInformation?.length !== 0 ? (
                    <>
                      {productMetadata?.pharmacaFullData?.additionalDosageInformation.map((a) => (
                        <PharmacText
                          key={a.code}
                          paddingSmallBottom
                        >
                          {a.displayName}
                        </PharmacText>
                      ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </ExtranetContainer>
                <ExtranetContainer
                  full
                  flexColumn
                  paddingSmallVertical
                >
                  <PharmacText weight="bold">{t('extranet:medicines.fimeaClass')}:</PharmacText>
                  {productMetadata?.fimea75Data?.medicinalSubstances ? (
                    <>
                      {productMetadata?.fimea75Data?.medicinalSubstances?.map((a) => (
                        <>
                          <ExtranetContainer paddingMediumTop>
                            <PharmacText weight="bold">{a.name}</PharmacText>
                          </ExtranetContainer>
                          <ExtranetContainer
                            key={a.id}
                            flexColumn
                            paddingMediumLeft
                          >
                            <ul>
                              <li>
                                <ExtranetContainer
                                  paddingSmallTop
                                  flexRow
                                >
                                  <PharmacText
                                    weight="bold"
                                    paddingSmallRight
                                  >
                                    {t('common:class')}:
                                  </PharmacText>
                                  <PharmacText paddingSmallBottom>{a.class}</PharmacText>
                                </ExtranetContainer>
                              </li>
                              <li>
                                <ExtranetContainer flexWrap>
                                  <PharmacText>
                                    <span
                                      style={{
                                        fontWeight: 'bold',
                                        paddingRight: 'var(--spacing-small)',
                                      }}
                                    >
                                      {t('common:comment')}:
                                    </span>
                                    {key({ fi: a.commentFi, en: a.commentEn, sv: a.commentSv })}
                                  </PharmacText>
                                </ExtranetContainer>
                              </li>
                            </ul>
                          </ExtranetContainer>
                        </>
                      ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </ExtranetContainer>
              </ExtranetContainer>
            </ExtranetContainer>
          </ExtranetContainer>
          <ExtranetContainer
            half={minDesktop}
            full={!minDesktop}
            justifyCenter
            marginLargeTop
            style={minDesktop ? { height: '100%', width: '40%' } : {}}
          >
            {photobankData?.images !== undefined && photobankData.images.length > 0 ? (
              <>
                <PharmacCarousel>
                  {photobankData.images?.map((a) => (
                    <PharmacCarouselItem key={a.url}>
                      <ImgContainer
                        style={{ marginBottom: 'var(--spacing-medium)' }}
                        onClick={() => showFullScreenImage(a.url)}
                      >
                        <CarouselImage
                          imageUrl={a.url}
                          hasShadow
                        />
                        <FullScreenIcon
                          size="25"
                          icon="maximize2"
                        />
                      </ImgContainer>
                    </PharmacCarouselItem>
                  ))}
                </PharmacCarousel>
              </>
            ) : medicineImages !== undefined && medicineImages.length > 0 ? (
              <>
                {medicineImages?.map((a) => (
                  <PharmacCarousel>
                    {a.urls.map((url) => (
                      <PharmacCarouselItem key={url}>
                        <ImgContainer
                          style={{ marginBottom: 'var(--spacing-medium)' }}
                          onClick={() => showFullScreenImage(url)}
                        >
                          <CarouselImage
                            imageUrl={url}
                            hasShadow
                          />
                          <FullScreenIcon
                            size="25"
                            icon="maximize2"
                          />
                        </ImgContainer>
                      </PharmacCarouselItem>
                    ))}
                  </PharmacCarousel>
                ))}
              </>
            ) : (
              <CarouselImage imageUrl={ImageNotAvailable} />
            )}
          </ExtranetContainer>
        </ExtranetContainer>

        <ExtranetContainer
          full
          flexColumn
          paddingLargeVertical
        >
          <ExtranetContainer
            full
            flexColumn
            paddingLargeHorizontal
          >
            <ExtranetContainer
              full
              marginLargeTop={minTablet && !minDesktop}
              marginLargeBottom
              flexColumn
            >
              <PharmacH4
                color="purple"
                paddingMediumBottom
              >
                {t('medicines.medicineManual')}
              </PharmacH4>
              <ExtranetContainer
                full
                style={{ borderBottom: '2px solid var(--color-primary)' }}
              />
            </ExtranetContainer>
            <ExtranetContainer
              flexRow={minDesktop}
              flexColumn={!minDesktop}
              full
            >
              <ExtranetContainer
                half={minDesktop}
                full={!minDesktop}
                flexColumn
                paddingLargeRight
              >
                <TextContainer>
                  <PharmacText weight="bold">{t('extranet:medicines.activeSubstance')}</PharmacText>
                  {productMetadata?.drugGuideDocument?.sections ? (
                    <>
                      {productMetadata?.drugGuideDocument?.sections
                        .filter((a) => a.id === '60200')
                        .map((b) => (
                          <PharmacText
                            style={{ maxWidth: '100%' }}
                            key={b.id}
                            dangerouslySetInnerHTML={{
                              __html: htmlStringSanitizer(b.content),
                            }}
                          />
                        ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </TextContainer>
                <TextContainer>
                  <PharmacText weight="bold">{t('extranet:medicines.purpose')}</PharmacText>
                  {productMetadata?.drugGuideDocument?.sections ? (
                    <>
                      {productMetadata?.drugGuideDocument?.sections
                        .filter((a) => a.id === '60300')
                        .map((b) => (
                          <PharmacText
                            key={b.id}
                            dangerouslySetInnerHTML={{
                              __html: htmlStringSanitizer(b.content),
                            }}
                          />
                        ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </TextContainer>
                <TextContainer>
                  <PharmacText weight="bold">{t('extranet:medicines.dosage')}</PharmacText>
                  {productMetadata?.drugGuideDocument?.sections ? (
                    <>
                      {productMetadata?.drugGuideDocument?.sections
                        .filter((a) => a.id === '60400')
                        .map((b) => (
                          <PharmacText
                            key={b.id}
                            dangerouslySetInnerHTML={{
                              __html: htmlStringSanitizer(b.content),
                            }}
                          />
                        ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </TextContainer>
                <TextContainer>
                  <PharmacText weight="bold">
                    {t('extranet:medicines.takingTheMedicine')}
                  </PharmacText>
                  {productMetadata?.drugGuideDocument?.sections ? (
                    <>
                      {productMetadata?.drugGuideDocument?.sections
                        .filter((a) => a.id === '60500')
                        .map((b) => (
                          <PharmacText
                            key={b.id}
                            dangerouslySetInnerHTML={{
                              __html: htmlStringSanitizer(b.content),
                            }}
                          />
                        ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </TextContainer>
                <TextContainer>
                  <PharmacText weight="bold">{t('extranet:medicines.influence')}</PharmacText>
                  {productMetadata?.drugGuideDocument?.sections ? (
                    <>
                      {productMetadata?.drugGuideDocument?.sections
                        .filter((a) => a.id === '60600')
                        .map((b) => (
                          <PharmacText
                            key={b.id}
                            dangerouslySetInnerHTML={{
                              __html: htmlStringSanitizer(b.content),
                            }}
                          />
                        ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </TextContainer>
              </ExtranetContainer>
              <ExtranetContainer
                half={minDesktop}
                full={!minDesktop}
                flexColumn
              >
                <TextContainer>
                  <PharmacText weight="bold">{t('extranet:medicines.sideEffects')}</PharmacText>
                  {productMetadata?.drugGuideDocument?.sections ? (
                    <>
                      {productMetadata?.drugGuideDocument?.sections
                        .filter((a) => a.id === '60700')
                        .map((b) => (
                          <PharmacText
                            key={b.id}
                            dangerouslySetInnerHTML={{
                              __html: htmlStringSanitizer(b.content),
                            }}
                          />
                        ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </TextContainer>
                <TextContainer>
                  <PharmacText weight="bold">{t('extranet:medicines.pregnancy')}</PharmacText>
                  {productMetadata?.drugGuideDocument?.sections ? (
                    <>
                      {productMetadata?.drugGuideDocument?.sections
                        .filter((a) => a.id === '60800')
                        .map((b) => (
                          <PharmacText
                            key={b.id}
                            dangerouslySetInnerHTML={{
                              __html: htmlStringSanitizer(b.content),
                            }}
                          />
                        ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </TextContainer>
                <TextContainer>
                  <PharmacText weight="bold">{t('extranet:medicines.storage')}</PharmacText>
                  {productMetadata?.drugGuideDocument?.sections ? (
                    <>
                      {productMetadata?.drugGuideDocument?.sections
                        .filter((a) => a.id === '60900')
                        .map((b) => (
                          <PharmacText
                            key={b.id}
                            dangerouslySetInnerHTML={{
                              __html: htmlStringSanitizer(b.content),
                            }}
                          />
                        ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </TextContainer>
                <TextContainer>
                  <PharmacText weight="bold">{t('extranet:medicines.appearance')}</PharmacText>
                  {productMetadata?.drugGuideDocument?.sections ? (
                    <>
                      {productMetadata?.drugGuideDocument?.sections
                        .filter((a) => a.id === '61000')
                        .map((b) => (
                          <PharmacText
                            key={b.id}
                            dangerouslySetInnerHTML={{
                              __html: htmlStringSanitizer(b.content),
                            }}
                          />
                        ))}
                    </>
                  ) : (
                    <PharmacText>{t('extranet:medicines.unknown')}</PharmacText>
                  )}
                </TextContainer>
              </ExtranetContainer>
            </ExtranetContainer>
          </ExtranetContainer>
        </ExtranetContainer>
      </AdditionalInfoContainer>
      {fullScreenUrl && (
        <ExtranetFullScreenImage
          url={fullScreenUrl}
          hide={closeFullScreenImage}
        />
      )}
    </ExtranetContainer>
  )
}
